'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Box, Typography, Button, Alert, useTheme, useMediaQuery, alpha, FormControlLabel, Switch } from '@mui/material';
import { useForm } from 'react-hook-form';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import CustomDialog from '@ui/components/common/Dialog';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
import { Icon } from '@iconify/react';
import { useEventRegistrationTranslation } from '@ui/layout/EventRegistration/hooks/useEventRegistrationTranslation';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import BDrawer from '@ui/components/common/BDrawer';
import StyledButtonGroupUndertaking from '@ui/components/ButtonGroup/UndertakingButtonGroup';
import DigitalSignature from '@ui/components/DigitalSignature';
import { REGISTRATION_FIELD_FILL_BG } from '../registrationFormFieldStyles';
const EDocumentPreview = ({ name, control, rules, error, fileName, defaultAcknowledgeReason, defaultDeclineReason, pdfUrl, onValueChange, readonly = false, acknowledgmentReason, reasonName, signatureName, undertakingToggleLabel, undertakingAlertNote, disabled = false }) => {
    return (_jsx(Controller, { name: name, control: control, rules: rules, defaultValue: undefined, render: ({ field }) => (_jsx(Controller, { name: reasonName || `${name}_reason`, control: control, defaultValue: '', render: ({ field: reasonField }) => (_jsx(Controller, { name: signatureName || `${name}_signature`, control: control, defaultValue: '', render: ({ field: signatureField }) => (_jsx(EDocumentPreviewContent, { value: field.value, onChange: field.onChange, onBlur: field.onBlur, error: error, disabled: disabled, fileName: fileName, defaultAcknowledgeReason: defaultAcknowledgeReason, defaultDeclineReason: defaultDeclineReason, pdfUrl: pdfUrl, onValueChange: onValueChange, readonly: readonly, acknowledgmentReason: acknowledgmentReason, reasonValue: reasonField.value, signatureValue: signatureField.value, onSignatureChange: signatureField.onChange, undertakingToggleLabel: undertakingToggleLabel, undertakingAlertNote: undertakingAlertNote })) })) })) }));
};
const EDocumentPreviewContent = ({ value, onChange, onBlur, error, fileName, defaultAcknowledgeReason, defaultDeclineReason, pdfUrl, onValueChange, readonly = false, acknowledgmentReason, reasonValue, signatureValue, onSignatureChange, undertakingToggleLabel, undertakingAlertNote, disabled }) => {
    const theme = useTheme();
    const { t } = useEventRegistrationTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const signatureRef = useRef(null);
    const [isClearDisabled, setIsClearDisabled] = useState(true);
    // Track if we're restoring signature to avoid saving during restoration
    const isRestoringRef = useRef(false);
    // Store the original signature when dialog opens to restore on cancel
    const originalSignatureRef = useRef('');
    const handleSignatureChange = (hasSignature) => {
        setIsClearDisabled(!hasSignature);
        // Don't save signature if we're in the middle of restoring it
        if (isRestoringRef.current) {
            return;
        }
        // Clear signature error when user starts drawing
        if (hasSignature) {
            setSignatureError('');
        }
        // Don't update form state when signature changes - only update on submit
        // This allows users to clear/draw without losing the original until they submit
    };
    const handleClearSignature = () => {
        signatureRef.current?.clearSignature();
        // Don't update form state - changes are only saved on submit
    };
    // Use translated defaults if not provided
    const acknowledgeReasonText = defaultAcknowledgeReason ||
        t('undertaking.defaultAcknowledgeReason') ||
        t('undertaking.acknowledgeRules');
    const declineReasonText = defaultDeclineReason ||
        t('undertaking.defaultDeclineReason') ||
        t('undertaking.declineUndertaking');
    // Determine status based on value from form
    const status = value === true ? 'acknowledged' : value === false ? 'declined' : 'view';
    // In readonly mode, if we have an acknowledgment reason, show the status
    const displayStatus = readonly && acknowledgmentReason
        ? value === true
            ? 'acknowledged'
            : value === false
                ? 'declined'
                : 'view'
        : status;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [acknowledgedReason, setAcknowledgedReason] = useState('');
    const [declinedReason, setDeclinedReason] = useState('');
    const [pdfBlobUrl, setPdfBlobUrl] = useState('');
    const [acknowledgeCheckbox, setAcknowledgeCheckbox] = useState(true); // Default checked
    const [signatureError, setSignatureError] = useState('');
    // Restore reason state from persisted form value
    useEffect(() => {
        if (reasonValue && reasonValue.trim() !== '') {
            if (value === true) {
                setAcknowledgedReason(reasonValue);
            }
            else if (value === false) {
                setDeclinedReason(reasonValue);
            }
        }
    }, [reasonValue, value]);
    // Set checkbox state based on current form value
    useEffect(() => {
        if (value === false) {
            setAcknowledgeCheckbox(false);
        }
        else if (value === true) {
            setAcknowledgeCheckbox(true);
        }
    }, [value]);
    const { control: dialogControl, setValue, handleSubmit, reset } = useForm({
        defaultValues: {
            reason: ''
        }
    });
    // Set the current reason in the dialog form when it opens
    useEffect(() => {
        if (dialogOpen) {
            if (value === false && reasonValue) {
                // If currently declined and has a reason, use the existing reason
                setValue('reason', reasonValue);
            }
            else if (value === false && !reasonValue) {
                // If currently declined but no reason, use default decline text
                setValue('reason', declineReasonText);
            }
            else {
                // If acknowledged or no value, clear the reason field
                setValue('reason', '');
            }
        }
    }, [dialogOpen, value, reasonValue, declineReasonText, setValue]);
    // Restore signature when dialog opens if signatureValue exists
    useEffect(() => {
        if (dialogOpen) {
            // Clear any previous signature errors when dialog opens
            setSignatureError('');
            // Store the original signature when dialog opens
            originalSignatureRef.current = signatureValue || '';
            if (signatureValue && signatureValue.trim() !== '') {
                // Small delay to ensure the canvas is rendered
                isRestoringRef.current = true;
                const timer = setTimeout(() => {
                    signatureRef.current?.setSignature(signatureValue);
                    setIsClearDisabled(false); // Enable clear button since signature exists
                    // Reset restoration flag after a delay to allow signature changes
                    setTimeout(() => {
                        isRestoringRef.current = false;
                    }, 200);
                }, 100);
                return () => {
                    clearTimeout(timer);
                    isRestoringRef.current = false;
                };
            }
            else {
                // If dialog opens and there's no signature, ensure clear button is disabled
                setIsClearDisabled(true);
                isRestoringRef.current = false;
            }
        }
    }, [dialogOpen, signatureValue]);
    useEffect(() => {
        if (pdfUrl && dialogOpen && !pdfBlobUrl) {
            fetch(pdfUrl, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    Accept: 'application/pdf,*/*'
                }
            })
                .then((response) => response.arrayBuffer())
                .then((arrayBuffer) => {
                // Create blob with correct MIME type
                const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfBlobUrl(url);
            })
                .catch((err) => {
                console.error('Error fetching PDF:', err);
            });
        }
    }, [pdfUrl, dialogOpen, pdfBlobUrl]);
    // Cleanup blob URL when dialog closes or component unmounts
    useEffect(() => {
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [pdfBlobUrl]);
    const handleViewClick = () => {
        setDialogOpen(true);
    };
    const handleChange = () => {
        setDialogOpen(true);
    };
    const handleClose = () => {
        // Restore form state to original signature when closing without submitting
        // The form state should already have the original signature since we don't update
        // it during dialog interactions, but we restore it just to be sure
        if (onSignatureChange && originalSignatureRef.current !== signatureValue) {
            onSignatureChange(originalSignatureRef.current);
        }
        setDialogOpen(false);
        reset();
        // Clean up blob URL when dialog closes
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl('');
        }
    };
    const handleAcknowledge = (data) => {
        // Validate signature is required when acknowledging
        const canvasSignature = signatureRef.current?.getSignature();
        if (!canvasSignature) {
            // Set error message
            setSignatureError(t('eventRegistration.form.fieldRequired'));
            return; // Don't proceed with submission
        }
        // Clear signature error
        setSignatureError('');
        const reason = acknowledgeReasonText; // Always use default acknowledge reason
        setAcknowledgedReason(reason);
        setDeclinedReason(''); // Clear decline reason when acknowledging
        const signature = canvasSignature || undefined;
        setDialogOpen(false);
        reset();
        // Use onValueChange callback to set both value, reason, and signature
        if (onValueChange) {
            setValue('reason', reason);
            // Always update signature field to match current canvas state
            if (onSignatureChange) {
                // If canvas has signature, save it; if canvas is empty, clear form state
                onSignatureChange(canvasSignature || '');
            }
            onValueChange(true, reason, signature);
        }
        else {
            onChange(true);
            // Always update signature field to match current canvas state
            if (onSignatureChange) {
                onSignatureChange(canvasSignature || '');
            }
        }
        // Trigger validation to clear any errors
        onBlur?.();
    };
    const handleDecline = (data) => {
        const reason = data.reason || declineReasonText;
        setDeclinedReason(reason);
        setAcknowledgedReason(''); // Clear acknowledge reason when declining
        // Clear signature when declining
        if (onSignatureChange) {
            onSignatureChange('');
        }
        setDialogOpen(false);
        reset();
        // Use onValueChange callback to set both value and reason (no signature for decline)
        if (onValueChange) {
            setValue('reason', reason);
            onValueChange(false, reason, undefined);
        }
        else {
            onChange(false);
        }
        // Trigger validation to clear any errors
        onBlur?.();
    };
    const actionButtons = (_jsx(Box, { sx: { display: 'flex', gap: 2 }, children: _jsx(StyledButtonGroupUndertaking, { sx: { mb: 1 }, buttons: [
                {
                    label: t('undertaking.decline'),
                    icon: _jsx(Icon, { icon: 'eva:close-fill', width: 22, height: 22 }),
                    colorType: 'error',
                    active: !acknowledgeCheckbox,
                    disabled: disabled,
                    onClick: () => {
                        if (readonly)
                            return;
                        setAcknowledgeCheckbox(false);
                        setValue('reason', declineReasonText);
                    }
                },
                {
                    label: t('undertaking.acknowledge'),
                    icon: _jsx(Icon, { icon: 'eva:checkmark-fill', width: 22, height: 22 }),
                    colorType: 'primary',
                    disabled: disabled,
                    active: acknowledgeCheckbox,
                    onClick: () => {
                        if (readonly)
                            return;
                        setAcknowledgeCheckbox(true);
                        setValue('reason', '');
                    }
                }
            ] }) }));
    const dialogActions = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { color: 'inherit', variant: 'outlined', size: 'large', onClick: handleClose, fullWidth: true, sx: { flex: 1 }, children: t('actions.cancel') }), _jsx(Button, { color: 'inherit', variant: 'contained', size: 'large', disabled: disabled, onClick: acknowledgeCheckbox ? handleSubmit(handleAcknowledge) : handleSubmit(handleDecline), fullWidth: true, sx: { flex: 1 }, children: t('eventRegistration.form.submit') })] }));
    const dialogContent = (_jsxs(Box, { sx: { px: isMobile ? 1 : 3, py: 1 }, children: [_jsx(Alert, { severity: 'info', variant: 'outlined', icon: _jsx(Icon, { icon: 'eva:info-fill' }), sx: {
                    '& .MuiAlert-icon': {
                        paddingTop: '10px'
                    },
                    mb: 3,
                    backgroundColor: alpha(theme.palette.info.main, 0.08)
                }, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: _jsx(Typography, { variant: 'body2', color: theme.palette.info.dark, children: undertakingAlertNote || t('undertaking.alert.line1') }) }) }), pdfUrl ? (_jsx(Box, { sx: {
                    width: '100%',
                    height: isMobile ? '400px' : '700px',
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden'
                }, children: pdfBlobUrl ? (_jsx("iframe", { src: pdfBlobUrl, style: {
                        width: '100%',
                        height: '100%',
                        border: 'none'
                    }, title: t('undertaking.pdfPreview') })) : null })) : (_jsx(Box, { sx: { p: 3, textAlign: 'center', bgcolor: 'background.paper' }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('undertaking.noPdfDocumentAvailable') }) })), _jsx(Box, { children: actionButtons }), (acknowledgeCheckbox || (readonly && value === false)) && (_jsxs(Box, { gap: 1, children: [_jsx(FormControlLabel, { control: _jsx(Switch, { checked: acknowledgeCheckbox, onChange: (e) => {
                                const isChecked = e.target.checked;
                                setAcknowledgeCheckbox(isChecked);
                                // When checkbox is unchecked (user wants to decline),
                                // automatically set the default decline text in the reason field
                                if (!isChecked) {
                                    setValue('reason', declineReasonText);
                                }
                                else {
                                    // When checkbox is checked (user wants to acknowledge),
                                    // clear the reason field as it's not needed for acknowledgment
                                    setValue('reason', '');
                                }
                            }, 
                            //disabled={readonly}
                            disabled: true, sx: {
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                    color: theme.palette.primary.main
                                }
                            } }), label: _jsx(Typography, { variant: 'body2', color: theme.palette.text.disabled, children: undertakingToggleLabel || t('undertaking.acknowledgeRules') }) }), _jsxs(Box, { mt: 1, children: [_jsx(Typography, { variant: 'body2', children: t('undertaking.signatureLabel') }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('undertaking.signatureInstructions') })] }), _jsxs(Box, { children: [_jsx(DigitalSignature, { ref: signatureRef, onSignatureChange: handleSignatureChange, disabled: disabled }), _jsx(Button, { variant: 'soft', color: 'primary', size: 'small', disabled: isClearDisabled, fullWidth: true, sx: { mt: 0.5, color: theme.palette.primary.dark }, onClick: handleClearSignature, children: t('undertaking.clearSignature') }), signatureError && _jsx(ErrorWithIcon, { error: signatureError })] })] })), (!acknowledgeCheckbox || (readonly && value === false)) && (_jsx(ControlledFilledTextField, { bg: 'grey8Event', name: 'reason', label: t('undertaking.reasonLabel'), control: dialogControl, placeholder: declineReasonText, multiline: true, rows: 3, disabled: readonly, slotProps: { inputLabel: { shrink: true } } }))] }));
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Box, { sx: {
                            borderRadius: '12px',
                            p: '12px',
                            bgcolor: REGISTRATION_FIELD_FILL_BG,
                            gap: 0.5,
                            display: 'flex',
                            flexDirection: 'column'
                        }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: [_jsx(Typography, { variant: 'body2', fontWeight: 400, color: theme.palette.text.primary, children: t('undertaking.label') }), displayStatus === 'view' && (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }, children: [_jsx(PdfIcon, { width: '22', height: '22' }), _jsx(Box, { flex: 1, minWidth: 0, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', fontWeight: 400, sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, children: fileName }) })] }), _jsx(Button, { variant: 'outlined', fullWidth: true, onClick: handleViewClick, disabled: readonly, children: t('undertaking.view') })] })), (displayStatus === 'acknowledged' || displayStatus === 'declined') && (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }, children: [_jsx(PdfIcon, { width: '22', height: '22' }), _jsx(Box, { flex: 1, minWidth: 0, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', fontWeight: 400, sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, children: fileName }) })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }, children: [_jsx(Icon, { icon: status === 'acknowledged' ? 'eva:checkmark-fill' : 'eva:close-fill', width: '22', height: '22', color: theme.palette.text.primary }), _jsx(Box, { children: _jsx(Typography, { variant: 'body2', color: 'text.primary', fontWeight: 400, children: displayStatus === 'acknowledged'
                                                            ? t('eventRegistration.form.undertakingSummaryAgree')
                                                            : t('eventRegistration.form.undertakingSummaryDecline') }) })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Button, { size: 'medium', variant: 'outlined', color: 'primary', fullWidth: true, disabled: true, startIcon: displayStatus === 'acknowledged' ? (_jsx(Icon, { icon: 'eva:checkmark-fill' })) : (_jsx(Icon, { icon: 'eva:close-fill' })), sx: {
                                                        '&.Mui-disabled .MuiButton-startIcon svg': {
                                                            color: 'currentColor'
                                                        }
                                                    }, children: displayStatus === 'acknowledged'
                                                        ? t('undertaking.acknowledged')
                                                        : t('undertaking.declined') }), readonly ? (_jsx(Button, { variant: 'outlined', color: 'success', size: 'medium', onClick: handleViewClick, sx: {
                                                        border: `1px solid ${theme.palette.success.dark}`,
                                                        color: theme.palette.success.dark
                                                    }, disabled: readonly, children: t('undertaking.view') })) : (_jsx(Button, { variant: 'outlined', color: 'primary', size: 'medium', onClick: handleChange, children: t('undertaking.change') }))] })] }))] }) }), error && _jsx(ErrorWithIcon, { error: error })] }), !isMobile && (_jsx(CustomDialog, { closeIcon: true, backgroundVariant: 'white', open: dialogOpen, onClose: handleClose, title: t('undertaking.dialogTitle'), maxWidth: 'md', actions: dialogActions, children: dialogContent })), isMobile && (_jsx(BDrawer, { title: t('undertaking.label'), anchor: 'bottom', open: dialogOpen, onClose: handleClose, showCloseButton: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) }))] }));
};
export default EDocumentPreview;
